import { makeAutoObservable } from 'mobx';
import { z } from 'zod';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FormModel, Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import {
    convertAndValidToFloat,
    validateMaxLength,
    validateNotEmpty,
} from 'src_common/common/mobx-utils/Form2/validators';
import { validateIsNumber } from 'src/api/utils/form2Validators/validateIsNumber';
import {
    MarkersOfHarmPopupPreviewContentType,
    MarkersOfHarmPopupType,
    PopupAppliedActionFormValueType,
    PopupAppliedActionRestrictionPeriodFormValueType,
    PopupButtonActionFormValueType,
    PopupButtonFormType,
    PopupRollingPeriodIsoFormValueType,
    PopupRiskLevelType,
    PopupTriggerParamType,
    PopupTriggerParamValueType,
    PopupTriggerSettingsFormType,
    PopupTriggerTemplateIdFormValueType,
    POPUP_TITLE_MAX_LENGTH,
    POPUP_BUTTON_TEXT_MAX_LENGTH,
    PopupTriggerTemplateType,
    showsAppliedActionRestrictionPeriod,
    isPopupRollingPeriodValue,
} from './MarkersOfHarmPopupTab.types';
import {
    MarkersOfHarmAppliedActionSettingsType,
    buildMarkersOfHarmAutomationDto,
} from './MarkersOfHarmPopupAutomation.mapper';
import { mapTriggerToFormValues } from './MarkersOfHarmPopupTab.mappers';

const REQUIRED_FIELD_MESSAGE = 'Required field';
const EMPTY_BUTTON_ACTION: PopupButtonActionFormValueType = '';
const EMPTY_APPLIED_ACTION: PopupAppliedActionFormValueType = '';
const EMPTY_APPLIED_ACTION_RESTRICTION_PERIOD: PopupAppliedActionRestrictionPeriodFormValueType = '';
const EMPTY_TRIGGER_TEMPLATE: PopupTriggerTemplateIdFormValueType = '';
const DURATION_HOURS_VALUES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'] as const;
const isDurationHoursValue = (value: string): boolean => DURATION_HOURS_VALUES.some((allowed) => allowed === value);

type TriggerScalarParamFieldType = {
    kind: 'scalar';
    param: PopupTriggerParamType;
    input: FormInputState<string, string>;
};

type TriggerBooleanParamFieldType = {
    kind: 'boolean';
    param: PopupTriggerParamType;
    input: FormInputState<string, string>;
};

type TriggerRollingPeriodParamFieldType = {
    kind: 'period-rolling';
    param: PopupTriggerParamType;
    input: FormInputState<PopupRollingPeriodIsoFormValueType, PopupRollingPeriodIsoFormValueType>;
};

type TriggerDurationParamFieldType = {
    kind: 'duration-hours';
    param: PopupTriggerParamType;
    input: FormInputState<string, string>;
};

type TriggerTimeRangeParamFieldType = {
    kind: 'period-time-range';
    param: PopupTriggerParamType;
    startInput: FormInputState<string, string>;
    endInput: FormInputState<string, string>;
};

export type TriggerParamFieldType =
    | TriggerScalarParamFieldType
    | TriggerBooleanParamFieldType
    | TriggerRollingPeriodParamFieldType
    | TriggerDurationParamFieldType
    | TriggerTimeRangeParamFieldType;

type MarkersOfHarmPopupFormModelType = {
    titleForm: string;
    bodyTextForm: string;
    triggerTypeForm: PopupTriggerTemplateIdFormValueType;
    primaryButtonTextForm: string;
    primaryButtonActionForm: PopupButtonActionFormValueType;
    secondaryButtonTextForm: string;
    secondaryButtonActionForm: PopupButtonActionFormValueType;
    appliedActionForm: PopupAppliedActionFormValueType;
    appliedActionRestrictionPeriodForm: PopupAppliedActionRestrictionPeriodFormValueType;
};

type FormSnapshotType = {
    title: string;
    bodyText: string;
    primaryButtonText: string;
    primaryButtonAction: PopupButtonActionFormValueType;
    primaryRedirectUrl: string;
    hasSecondaryButton: boolean;
    secondaryButtonText: string;
    secondaryButtonAction: PopupButtonActionFormValueType;
    secondaryRedirectUrl: string;
    triggerType: PopupTriggerTemplateIdFormValueType;
    triggerParams: string;
    appliedAction: PopupAppliedActionFormValueType;
    appliedActionRestrictionPeriod: PopupAppliedActionRestrictionPeriodFormValueType;
    riskLevel: PopupRiskLevelType;
    editingActive: boolean;
};

const areSnapshotsEqual = (left: FormSnapshotType, right: FormSnapshotType): boolean =>
    left.title === right.title &&
    left.bodyText === right.bodyText &&
    left.primaryButtonText === right.primaryButtonText &&
    left.primaryButtonAction === right.primaryButtonAction &&
    left.primaryRedirectUrl === right.primaryRedirectUrl &&
    left.hasSecondaryButton === right.hasSecondaryButton &&
    left.secondaryButtonText === right.secondaryButtonText &&
    left.secondaryButtonAction === right.secondaryButtonAction &&
    left.secondaryRedirectUrl === right.secondaryRedirectUrl &&
    left.triggerType === right.triggerType &&
    left.triggerParams === right.triggerParams &&
    left.appliedAction === right.appliedAction &&
    left.appliedActionRestrictionPeriod === right.appliedActionRestrictionPeriod &&
    left.riskLevel === right.riskLevel &&
    left.editingActive === right.editingActive;

const validateTriggerTemplateSelected = (
    value: PopupTriggerTemplateIdFormValueType
): Result<PopupTriggerTemplateIdFormValueType> => {
    if (value === '') {
        return Result.createError(REQUIRED_FIELD_MESSAGE);
    }

    return Result.createOk(value);
};

const validateAppliedActionSelected = (
    value: PopupAppliedActionFormValueType
): Result<PopupAppliedActionFormValueType> => {
    if (value === '') {
        return Result.createError(REQUIRED_FIELD_MESSAGE);
    }

    return Result.createOk(value);
};

const validateAppliedActionRestrictionPeriod =
    (getAppliedAction: () => PopupAppliedActionFormValueType) =>
    (
        value: PopupAppliedActionRestrictionPeriodFormValueType
    ): Result<PopupAppliedActionRestrictionPeriodFormValueType> => {
        if (!showsAppliedActionRestrictionPeriod(getAppliedAction())) {
            return Result.createOk(value);
        }

        if (value === '') {
            return Result.createError(REQUIRED_FIELD_MESSAGE);
        }

        return Result.createOk(value);
    };

const validateButtonActionSelected = (
    value: PopupButtonActionFormValueType
): Result<PopupButtonActionFormValueType> => {
    if (value === '') {
        return Result.createError(REQUIRED_FIELD_MESSAGE);
    }

    return Result.createOk(value);
};

const validateSecondaryButtonText =
    (getHasSecondaryButton: () => boolean) =>
    (value: string): Result<string> => {
        if (!getHasSecondaryButton()) {
            return Result.createOk(value);
        }

        return validateNotEmpty(REQUIRED_FIELD_MESSAGE)(value);
    };

const validateSecondaryButtonAction =
    (getHasSecondaryButton: () => boolean) =>
    (value: PopupButtonActionFormValueType): Result<PopupButtonActionFormValueType> => {
        if (!getHasSecondaryButton()) {
            return Result.createOk(value);
        }

        return validateButtonActionSelected(value);
    };

const isRedirectAction = (action: PopupButtonActionFormValueType): boolean => action === 'redirect-to-page';
const isRichTextFilled = (value: string): boolean =>
    value
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim() !== '';
const isTextFilled = (value: string): boolean => value.trim() !== '';
const isValidTimeValue = (value: string): boolean => /^([01]\d|2[0-3]):([0-5]\d)$/.test(value);

const validateRedirectUrlWhenNeeded =
    (getAction: () => PopupButtonActionFormValueType) =>
    (value: string): Result<string> => {
        if (!isRedirectAction(getAction())) {
            return Result.createOk(value);
        }

        return validateNotEmpty(REQUIRED_FIELD_MESSAGE)(value);
    };

const hasAllowedKind = (param: PopupTriggerParamType, kind: string): boolean =>
    Array.isArray(param.allowedKinds) && param.allowedKinds.includes(kind);

const validateScalarParamValue =
    (param: PopupTriggerParamType) =>
    (value: string): Result<string> => {
        const trimmedValue = value.trim();
        if (trimmedValue === '') {
            return Result.createError(REQUIRED_FIELD_MESSAGE);
        }

        if (param.type === 'integer') {
            if (validateIsNumber(trimmedValue).value.type === 'error') {
                return Result.createError('Must be a valid whole number');
            }
            const parsed = parseFloat(trimmedValue);
            if (parsed <= 0) {
                return Result.createError('Must be a positive number');
            }
        }

        if (param.type === 'numeric' || param.type === 'duration') {
            const decimalResult = convertAndValidToFloat('Must be a valid decimal number')(trimmedValue);
            if (decimalResult.value.type === 'error') {
                return decimalResult;
            }
            const parsed = parseFloat(trimmedValue.replace(',', '.'));
            if (parsed <= 0) {
                return Result.createError('Must be a positive number');
            }
        }

        return Result.createOk(trimmedValue);
    };

const validateBooleanParamValue = (value: string): Result<string> => {
    if (value === '') {
        return Result.createError(REQUIRED_FIELD_MESSAGE);
    }

    return Result.createOk(value);
};

const validateRollingPeriodValue = (
    value: PopupRollingPeriodIsoFormValueType
): Result<PopupRollingPeriodIsoFormValueType> => {
    if (value === '') {
        return Result.createError(REQUIRED_FIELD_MESSAGE);
    }

    return Result.createOk(value);
};

const validateDurationHoursValue = (value: string): Result<string> => {
    if (value === '') {
        return Result.createError(REQUIRED_FIELD_MESSAGE);
    }

    if (!isDurationHoursValue(value)) {
        return Result.createError('Choose between 1 and 12 hours');
    }

    return Result.createOk(value);
};

const validateTimeRangeStart = (value: string): Result<string> => {
    const trimmedValue = value.trim();
    if (trimmedValue === '') {
        return Result.createError(REQUIRED_FIELD_MESSAGE);
    }
    if (!isValidTimeValue(trimmedValue)) {
        return Result.createError('Use HH:mm format');
    }
    return Result.createOk(trimmedValue);
};

const validateTimeRangeEnd =
    (getStartValue: () => string) =>
    (value: string): Result<string> => {
        const trimmedValue = value.trim();
        if (trimmedValue === '') {
            return Result.createError(REQUIRED_FIELD_MESSAGE);
        }
        if (!isValidTimeValue(trimmedValue)) {
            return Result.createError('Use HH:mm format');
        }

        const startValue = getStartValue().trim();
        if (isValidTimeValue(startValue) && startValue === trimmedValue) {
            return Result.createError('End time must differ from start time');
        }

        return Result.createOk(trimmedValue);
    };

const toInputStringValue = (value: unknown): string => (typeof value === 'string' ? value : String(value ?? ''));
const getObjectValue = (value: unknown): Record<string, unknown> | null =>
    typeof value === 'object' && value !== null ? Object.fromEntries(Object.entries(value)) : null;
const finiteNumberSchema = z.number().finite();

const getTimeRangeValue = (value: unknown): { start?: string; end?: string } | null => {
    const obj = getObjectValue(value);
    if (obj === null || obj.type !== 'time-range') {
        return null;
    }

    return {
        start: typeof obj.start === 'string' ? obj.start : undefined,
        end: typeof obj.end === 'string' ? obj.end : undefined,
    };
};

const getRollingValue = (value: unknown): string | null => {
    const obj = getObjectValue(value);
    if (obj === null || obj.type !== 'rolling' || typeof obj.value !== 'string') {
        return null;
    }

    return obj.value;
};

const parseDurationHoursValue = (value: unknown): string | null => {
    if (typeof value === 'number' && finiteNumberSchema.safeParse(value).success) {
        const normalized = String(Math.trunc(value));
        return isDurationHoursValue(normalized) ? normalized : null;
    }

    const objectValue = getObjectValue(value);
    if (objectValue === null || !('hours' in objectValue)) {
        return null;
    }

    return parseDurationHoursValue(objectValue.hours);
};

export class MarkersOfHarmPopupFormState {
    public readonly titleForm: FormInputState<string, string>;
    public readonly bodyTextForm: FormInputState<string, string>;
    public readonly triggerTypeForm: FormInputState<
        PopupTriggerTemplateIdFormValueType,
        PopupTriggerTemplateIdFormValueType
    >;
    public readonly primaryButtonTextForm: FormInputState<string, string>;
    public readonly primaryButtonActionForm: FormInputState<
        PopupButtonActionFormValueType,
        PopupButtonActionFormValueType
    >;
    public readonly primaryRedirectUrlForm: FormInputState<string, string>;
    public readonly secondaryButtonTextForm: FormInputState<string, string>;
    public readonly secondaryButtonActionForm: FormInputState<
        PopupButtonActionFormValueType,
        PopupButtonActionFormValueType
    >;
    public readonly secondaryRedirectUrlForm: FormInputState<string, string>;
    public readonly appliedActionForm: FormInputState<PopupAppliedActionFormValueType, PopupAppliedActionFormValueType>;
    public readonly appliedActionRestrictionPeriodForm: FormInputState<
        PopupAppliedActionRestrictionPeriodFormValueType,
        PopupAppliedActionRestrictionPeriodFormValueType
    >;
    private readonly form: FormModel<MarkersOfHarmPopupFormModelType>;
    public triggerTemplates: PopupTriggerTemplateType[] = [];
    public triggerParamFields: TriggerParamFieldType[] = [];
    /** Bumped whenever trigger param fields are rebuilt so Select remounts with fresh FormInputState. */
    public triggerParamFieldsVersion = 0;
    public missingTriggerTemplateForEdit = false;
    public hasSecondaryButton = false;
    public riskLevel: PopupRiskLevelType = 'low';
    public editingPopupId: string | null = null;
    public editingTriggerId: number | null = null;
    public editingAutomationName: string | null = null;
    public editingActive = true;
    private initialSnapshot: FormSnapshotType;

    public constructor() {
        makeAutoObservable(this);

        this.titleForm = FormInputState.new('')
            .map(validateNotEmpty(REQUIRED_FIELD_MESSAGE))
            .map(validateMaxLength(POPUP_TITLE_MAX_LENGTH));
        this.bodyTextForm = FormInputState.new('').map(validateNotEmpty(REQUIRED_FIELD_MESSAGE));
        this.triggerTypeForm = FormInputState.new(EMPTY_TRIGGER_TEMPLATE).map(validateTriggerTemplateSelected);
        this.primaryButtonTextForm = FormInputState.new('')
            .map(validateNotEmpty(REQUIRED_FIELD_MESSAGE))
            .map(validateMaxLength(POPUP_BUTTON_TEXT_MAX_LENGTH));
        this.primaryButtonActionForm = FormInputState.new(EMPTY_BUTTON_ACTION).map(validateButtonActionSelected);
        this.primaryRedirectUrlForm = FormInputState.new('').map(
            validateRedirectUrlWhenNeeded(() => this.primaryButtonActionForm.value)
        );
        this.secondaryButtonTextForm = FormInputState.new('')
            .map(validateSecondaryButtonText(() => this.hasSecondaryButton))
            .map(validateMaxLength(POPUP_BUTTON_TEXT_MAX_LENGTH));
        this.secondaryButtonActionForm = FormInputState.new(EMPTY_BUTTON_ACTION).map(
            validateSecondaryButtonAction(() => this.hasSecondaryButton)
        );
        this.secondaryRedirectUrlForm = FormInputState.new('').map(
            validateRedirectUrlWhenNeeded(() => this.secondaryButtonActionForm.value)
        );
        this.appliedActionForm = FormInputState.new(EMPTY_APPLIED_ACTION).map(validateAppliedActionSelected);
        this.appliedActionRestrictionPeriodForm = FormInputState.new(EMPTY_APPLIED_ACTION_RESTRICTION_PERIOD).map(
            validateAppliedActionRestrictionPeriod(() => this.appliedActionForm.value)
        );

        this.form = FormModel.group({
            titleForm: this.titleForm,
            bodyTextForm: this.bodyTextForm,
            triggerTypeForm: this.triggerTypeForm,
            primaryButtonTextForm: this.primaryButtonTextForm,
            primaryButtonActionForm: this.primaryButtonActionForm,
            secondaryButtonTextForm: this.secondaryButtonTextForm,
            secondaryButtonActionForm: this.secondaryButtonActionForm,
            appliedActionForm: this.appliedActionForm,
            appliedActionRestrictionPeriodForm: this.appliedActionRestrictionPeriodForm,
        });

        this.initialSnapshot = this.captureSnapshot();
    }

    public get hasUnsavedChanges(): boolean {
        return !areSnapshotsEqual(this.captureSnapshot(), this.initialSnapshot);
    }

    public get selectedTriggerTemplate(): PopupTriggerTemplateType | null {
        if (this.triggerTypeForm.value === '') {
            return null;
        }

        return this.triggerTemplates.find((template) => template.id === this.triggerTypeForm.value) ?? null;
    }

    public setTriggerTemplates = (templates: PopupTriggerTemplateType[]): void => {
        this.triggerTemplates = templates;

        if (this.triggerTypeForm.value !== '' && this.selectedTriggerTemplate === null) {
            this.triggerTypeForm.setValue('');
            this.triggerParamFields = [];
        }
    };

    public get showAppliedActionRestrictionPeriod(): boolean {
        return showsAppliedActionRestrictionPeriod(this.appliedActionForm.value);
    }

    public get showPrimaryRedirectUrl(): boolean {
        return isRedirectAction(this.primaryButtonActionForm.value);
    }

    public get showSecondaryRedirectUrl(): boolean {
        return this.hasSecondaryButton && isRedirectAction(this.secondaryButtonActionForm.value);
    }

    public get isEditMode(): boolean {
        return this.editingPopupId !== null;
    }

    public get triggerTemplateOptions(): Array<{ label: string; value: string; description?: string }> {
        return this.triggerTemplates.map((template) => ({
            label: template.displayName,
            value: template.id,
            description: template.description,
        }));
    }

    public get triggerTemplateSummaryLabel(): string | null {
        return this.selectedTriggerTemplate?.displayName ?? null;
    }

    public get triggerParamSummaryValues(): Array<{ label: string; value: string }> {
        return this.triggerParamFields.flatMap((field) => {
            if (field.kind === 'period-time-range') {
                const start = field.startInput.value.trim();
                const end = field.endInput.value.trim();
                if (start === '' || end === '') {
                    return [];
                }

                return [{ label: field.param.displayName, value: `${start}-${end}` }];
            }

            if (field.input.value === '') {
                return [];
            }

            return [{ label: field.param.displayName, value: String(field.input.value) }];
        });
    }

    public setRiskLevel = (value: PopupRiskLevelType): void => {
        this.riskLevel = value;
    };

    public resetAppliedActionFields = (): void => {
        this.appliedActionForm.setValue('');
        this.appliedActionRestrictionPeriodForm.setValue('');
        this.appliedActionForm.resetIsVisited();
        this.appliedActionRestrictionPeriodForm.resetIsVisited();
    };

    public onAppliedActionOptionClick = (): void => {
        this.appliedActionRestrictionPeriodForm.setValue('');
        this.appliedActionRestrictionPeriodForm.resetIsVisited();
    };

    public loadAppliedActionSettings = (settings: MarkersOfHarmAppliedActionSettingsType): void => {
        this.appliedActionForm.setValue(settings.appliedAction);
        this.appliedActionRestrictionPeriodForm.setValue(settings.restrictionPeriod ?? '');
    };

    public buildAppliedActionSettings = (): MarkersOfHarmAppliedActionSettingsType | null => {
        if (this.appliedActionForm.value === '') {
            return null;
        }

        return {
            appliedAction: this.appliedActionForm.value,
            restrictionPeriod: this.appliedActionRestrictionPeriodForm.value,
        };
    };

    private createParamField = (
        param: PopupTriggerParamType,
        value: unknown,
        template: PopupTriggerTemplateType
    ): TriggerParamFieldType => {
        if (param.type === 'period' && hasAllowedKind(param, 'time-range')) {
            const timeRangeValue = getTimeRangeValue(value);
            const startInput = FormInputState.new(timeRangeValue?.start ?? '').map(validateTimeRangeStart);
            const endInput = FormInputState.new(timeRangeValue?.end ?? '').map(
                validateTimeRangeEnd(() => startInput.value)
            );

            return {
                kind: 'period-time-range',
                param,
                startInput,
                endInput,
            };
        }

        if (param.type === 'period' && hasAllowedKind(param, 'rolling')) {
            const rollingValue = getRollingValue(value);
            const defaultValue: PopupRollingPeriodIsoFormValueType =
                rollingValue !== null && isPopupRollingPeriodValue(rollingValue, template) ? rollingValue : '';

            return {
                kind: 'period-rolling',
                param,
                input: FormInputState.new(defaultValue).map(validateRollingPeriodValue),
            };
        }

        if (param.type === 'duration') {
            const defaultValue = parseDurationHoursValue(value) ?? '';
            return {
                kind: 'duration-hours',
                param,
                input: FormInputState.new(defaultValue).map(validateDurationHoursValue),
            };
        }

        if (param.type === 'boolean') {
            const defaultValue = typeof value === 'boolean' ? (value ? 'true' : 'false') : '';

            return {
                kind: 'boolean',
                param,
                input: FormInputState.new(defaultValue).map(validateBooleanParamValue),
            };
        }

        return {
            kind: 'scalar',
            param,
            input: FormInputState.new(toInputStringValue(value)).map(validateScalarParamValue(param)),
        };
    };

    private buildParamFieldsForTemplate = (
        template: PopupTriggerTemplateType | null,
        existingParams?: Record<string, PopupTriggerParamValueType>
    ): void => {
        if (template === null) {
            this.triggerParamFields = [];
            this.triggerParamFieldsVersion += 1;
            return;
        }

        this.triggerParamFields = template.params.map((param) =>
            this.createParamField(param, existingParams?.[param.name], template)
        );
        this.triggerParamFieldsVersion += 1;
    };

    private getSerializedTriggerParams = (): string => {
        const serialized = this.triggerParamFields.map((field) => {
            if (field.kind === 'period-time-range') {
                return `${field.param.name}:${field.startInput.value}-${field.endInput.value}`;
            }

            return `${field.param.name}:${field.input.value}`;
        });

        return serialized.join('|');
    };

    private captureSnapshot = (): FormSnapshotType => ({
        title: this.titleForm.value,
        bodyText: this.bodyTextForm.value,
        primaryButtonText: this.primaryButtonTextForm.value,
        primaryButtonAction: this.primaryButtonActionForm.value,
        primaryRedirectUrl: this.primaryRedirectUrlForm.value,
        hasSecondaryButton: this.hasSecondaryButton,
        secondaryButtonText: this.secondaryButtonTextForm.value,
        secondaryButtonAction: this.secondaryButtonActionForm.value,
        secondaryRedirectUrl: this.secondaryRedirectUrlForm.value,
        triggerType: this.triggerTypeForm.value,
        triggerParams: this.getSerializedTriggerParams(),
        appliedAction: this.appliedActionForm.value,
        appliedActionRestrictionPeriod: this.appliedActionRestrictionPeriodForm.value,
        riskLevel: this.riskLevel,
        editingActive: this.editingActive,
    });

    private saveSnapshot = (): void => {
        this.initialSnapshot = this.captureSnapshot();
    };

    public markCurrentStateAsSaved = (): void => {
        this.saveSnapshot();
    };

    public loadFromPopup = (popup: MarkersOfHarmPopupType): void => {
        this.editingPopupId = popup.id;
        this.editingTriggerId = popup.trigger?.id ?? null;
        this.editingAutomationName = popup.trigger?.actions[0]?.name ?? popup.trigger?.name ?? null;
        this.editingActive = popup.active;
        this.missingTriggerTemplateForEdit = false;

        this.titleForm.setValue(popup.title);
        this.bodyTextForm.setValue(popup.bodyText);
        this.primaryButtonTextForm.setValue(popup.primaryButton.text);
        this.primaryButtonActionForm.setValue(popup.primaryButton.action);
        this.primaryRedirectUrlForm.setValue(popup.primaryButton.redirectUrl ?? '');

        if (popup.secondaryButton === undefined) {
            this.hasSecondaryButton = false;
            this.secondaryButtonTextForm.setValue('');
            this.secondaryButtonActionForm.setValue(EMPTY_BUTTON_ACTION);
            this.secondaryRedirectUrlForm.setValue('');
        } else {
            this.hasSecondaryButton = true;
            this.secondaryButtonTextForm.setValue(popup.secondaryButton.text);
            this.secondaryButtonActionForm.setValue(popup.secondaryButton.action);
            this.secondaryRedirectUrlForm.setValue(popup.secondaryButton.redirectUrl ?? '');
        }

        const selectedTemplate =
            popup.trigger === undefined
                ? null
                : (this.triggerTemplates.find((template) => template.id === popup.trigger?.templateId) ?? null);
        const triggerSettings =
            popup.trigger === undefined ? null : mapTriggerToFormValues(popup.trigger, selectedTemplate);

        if (popup.trigger !== undefined && selectedTemplate === null) {
            this.missingTriggerTemplateForEdit = true;
        }

        if (triggerSettings === null) {
            this.resetTriggerSettings();
        } else {
            this.triggerTypeForm.setValue(triggerSettings.templateId);
            this.buildParamFieldsForTemplate(selectedTemplate, triggerSettings.params);
            this.riskLevel = popup.trigger?.riskLevel ?? 'low';
        }

        this.form.resetIsVisited();
        this.form.resetIsTouched();
        this.primaryRedirectUrlForm.resetIsVisited();
        this.secondaryRedirectUrlForm.resetIsVisited();
        this.triggerParamFields.forEach((field) => {
            if (field.kind === 'period-time-range') {
                field.startInput.resetIsVisited();
                field.endInput.resetIsVisited();
                return;
            }

            field.input.resetIsVisited();
        });
        this.saveSnapshot();
    };

    public onTriggerTypeOptionClick = (nextTriggerType: PopupTriggerTemplateIdFormValueType): void => {
        if (nextTriggerType === this.triggerTypeForm.value) {
            return;
        }

        this.triggerTypeForm.setValue(nextTriggerType);
        this.missingTriggerTemplateForEdit = false;

        const nextTemplate = this.triggerTemplates.find((template) => template.id === nextTriggerType) ?? null;
        this.buildParamFieldsForTemplate(nextTemplate);
    };

    private resetTriggerSettings = (): void => {
        this.triggerTypeForm.setValue('');
        this.triggerParamFields = [];
        this.missingTriggerTemplateForEdit = false;
    };

    public get hasDisabledTemplateSelectorDescription(): boolean {
        return this.isEditMode;
    }

    public buildTriggerSettingsFormValues = (): PopupTriggerSettingsFormType | null => {
        const selectedTemplate = this.selectedTriggerTemplate;
        if (selectedTemplate === null) {
            return null;
        }

        const params: Record<string, PopupTriggerParamValueType> = {};

        for (const field of this.triggerParamFields) {
            if (field.kind === 'scalar') {
                const trimmed = field.input.value.trim();
                if (trimmed === '') {
                    return null;
                }

                if (field.param.type === 'integer') {
                    params[field.param.name] = parseInt(trimmed, 10);
                } else if (field.param.type === 'numeric' || field.param.type === 'duration') {
                    params[field.param.name] = parseFloat(trimmed.replace(',', '.'));
                } else {
                    params[field.param.name] = trimmed;
                }
                continue;
            }

            if (field.kind === 'boolean') {
                if (field.input.value === '') {
                    return null;
                }
                params[field.param.name] = field.input.value === 'true';
                continue;
            }

            if (field.kind === 'period-rolling') {
                if (field.input.value === '') {
                    return null;
                }
                params[field.param.name] = { type: 'rolling', value: field.input.value };
                continue;
            }

            if (field.kind === 'duration-hours') {
                if (field.input.value === '') {
                    return null;
                }
                params[field.param.name] = parseInt(field.input.value, 10);
                continue;
            }

            const start = field.startInput.value.trim();
            const end = field.endInput.value.trim();
            if (start === '' || end === '') {
                return null;
            }
            params[field.param.name] = { type: 'time-range', start, end };
        }

        return {
            templateId: selectedTemplate.id,
            params,
        };
    };

    public cancelEdit = (): void => {
        this.editingPopupId = null;
        this.editingTriggerId = null;
        this.editingActive = true;
        this.resetForm();
    };

    public removeSecondaryButton = (): void => {
        this.hasSecondaryButton = false;
        this.secondaryButtonTextForm.reset();
        this.secondaryButtonActionForm.setValue('');
        this.secondaryRedirectUrlForm.reset();
        this.secondaryButtonActionForm.resetIsVisited();
    };

    public addSecondaryButton = (): void => {
        this.hasSecondaryButton = true;
    };

    public get isFormValid(): boolean {
        return this.computeFormValidity();
    }

    private areTriggerParamFieldsValid = (): boolean => {
        return this.triggerParamFields.every((field) => {
            if (field.kind === 'period-time-range') {
                return field.startInput.result.value.type === 'ok' && field.endInput.result.value.type === 'ok';
            }

            return field.input.result.value.type === 'ok';
        });
    };

    private computeFormValidity(): boolean {
        const baseValid = this.form.result.value.type === 'ok';
        const primaryRedirectValid =
            !isRedirectAction(this.primaryButtonActionForm.value) ||
            this.primaryRedirectUrlForm.result.value.type === 'ok';
        const secondaryValid =
            !this.hasSecondaryButton ||
            (this.secondaryButtonTextForm.result.value.type === 'ok' &&
                this.secondaryButtonActionForm.result.value.type === 'ok' &&
                (!isRedirectAction(this.secondaryButtonActionForm.value) ||
                    this.secondaryRedirectUrlForm.result.value.type === 'ok'));

        if (!(baseValid && primaryRedirectValid && secondaryValid && this.areTriggerParamFieldsValid())) {
            return false;
        }

        const triggerSettings = this.buildTriggerSettingsFormValues();
        const appliedActionSettings = this.buildAppliedActionSettings();

        if (triggerSettings === null || appliedActionSettings === null) {
            return false;
        }

        return (
            buildMarkersOfHarmAutomationDto({
                automationName: 'moh-preview',
                popupReference: 'moh-preview',
                popupTitle: this.titleForm.value,
                active: this.isEditMode ? this.editingActive : true,
                appliedActionSettings,
            }) !== null
        );
    }

    public validateForm(): boolean {
        this.form.setAsVisited();

        if (isRedirectAction(this.primaryButtonActionForm.value)) {
            this.primaryRedirectUrlForm.setAsVisited();
        }

        if (this.hasSecondaryButton) {
            this.secondaryButtonTextForm.setAsVisited();
            this.secondaryButtonActionForm.setAsVisited();
            if (isRedirectAction(this.secondaryButtonActionForm.value)) {
                this.secondaryRedirectUrlForm.setAsVisited();
            }
        }

        this.triggerParamFields.forEach((field) => {
            if (field.kind === 'period-time-range') {
                field.startInput.setAsVisited();
                field.endInput.setAsVisited();
                return;
            }

            field.input.setAsVisited();
        });

        if (this.showAppliedActionRestrictionPeriod) {
            this.appliedActionRestrictionPeriodForm.setAsVisited();
        }

        return this.computeFormValidity();
    }

    public buildPreviewContent = (): MarkersOfHarmPopupPreviewContentType | null => {
        const preview: MarkersOfHarmPopupPreviewContentType = {};

        if (isTextFilled(this.titleForm.value)) {
            preview.title = this.titleForm.value.trim();
        }

        if (isRichTextFilled(this.bodyTextForm.value)) {
            preview.bodyText = this.bodyTextForm.value;
        }

        if (isTextFilled(this.primaryButtonTextForm.value)) {
            preview.primaryButton = {
                text: this.primaryButtonTextForm.value.trim(),
            };
        }

        if (this.hasSecondaryButton && isTextFilled(this.secondaryButtonTextForm.value)) {
            preview.secondaryButton = {
                text: this.secondaryButtonTextForm.value.trim(),
            };
        }

        if (preview.title === undefined && preview.bodyText === undefined) {
            return null;
        }

        return preview;
    };

    public buildPopupPayload(): Omit<
        MarkersOfHarmPopupType,
        'id' | 'createdAt' | 'activationDate' | 'deactivationDate' | 'trigger' | 'triggerId'
    > {
        const primaryButtonAction = this.primaryButtonActionForm.value;
        if (primaryButtonAction === '') {
            throw new Error('Primary button action is required.');
        }

        const primaryButton: PopupButtonFormType = {
            text: this.primaryButtonTextForm.value,
            action: primaryButtonAction,
        };

        if (isRedirectAction(primaryButtonAction)) {
            primaryButton.redirectUrl = this.primaryRedirectUrlForm.value.trim();
        }

        let secondaryButton: PopupButtonFormType | undefined;

        if (this.hasSecondaryButton) {
            const secondaryButtonAction = this.secondaryButtonActionForm.value;
            if (secondaryButtonAction === '') {
                throw new Error('Secondary button action is required.');
            }

            const secondaryButtonPayload: PopupButtonFormType = {
                text: this.secondaryButtonTextForm.value,
                action: secondaryButtonAction,
            };

            if (isRedirectAction(secondaryButtonAction)) {
                secondaryButtonPayload.redirectUrl = this.secondaryRedirectUrlForm.value.trim();
            }

            secondaryButton = secondaryButtonPayload;
        }

        return {
            title: this.titleForm.value.trim(),
            bodyText: this.bodyTextForm.value,
            primaryButton,
            secondaryButton,
            active: this.isEditMode ? this.editingActive : true,
        };
    }

    public resetForm = (): void => {
        this.editingPopupId = null;
        this.editingTriggerId = null;
        this.editingAutomationName = null;
        this.editingActive = true;
        this.riskLevel = 'low';
        this.hasSecondaryButton = false;
        this.missingTriggerTemplateForEdit = false;
        this.triggerParamFields = [];
        this.form.reset();
        this.primaryRedirectUrlForm.reset();
        this.secondaryRedirectUrlForm.reset();
        this.saveSnapshot();
    };
}
